import { type V2, TAU, angleTo, dist } from '../../core/math';
import { Rng } from '../../core/rng';
import { polylineDistance } from '../../zones/build';
import { archway, disc, edgeRise, isOpen, lampsAlong, lifeNodes, openSpots, paint, place, placeFacing, ridge, ringOf, wall } from '../../zones/townKit';
import type { ZoneLayout } from '../../zones/types';
import { ZoneBuilder } from '../../zones/ZoneBuilder';

/**
 * FROSTHOLLOW (levels 21–30) — a snowbound gothic valley east of Sunscar.
 * The pass from Sunscar (west) descends to the Abbey of the Hollow Bell, the
 * hub, with its graveyard to the south-east. The frozen lake fills the valley
 * floor; a drowned chapel stands on its north shore. Pine forests hide the
 * wolf packs and Rimefang Alpha's den (east); yetis roam the north-east
 * highlands below Frostfang Keep.
 */
export function buildFrosthollow(): ZoneLayout {
  const rng = new Rng('frosthollow');
  const b = new ZoneBuilder(
    {
      id: 'frosthollow', name: 'Frosthollow', subtitle: 'Where the bell tolls for the frozen dead', kind: 'field', biome: 'frost',
      levelRange: [21, 30], music: 'frost', width: 240, depth: 240, seed: 4101,
      ground: ['snow', 'dirt', 'rock', 'ice'], groundScale: [6, 5, 7, 6],
      hills: 4, hillScale: 0.016, slopeLayer: 2, patchLayer: 2, patchAmount: -0.04, waterLevel: -6, ambient: 'snow', mapTint: 0x8a9aae,
    },
    rng,
  );
  const PI = Math.PI;
  const W_EXIT: V2 = { x: -114, z: 60 };
  const KEEP: V2 = { x: 86, z: -98 };
  const ABBEY: V2 = { x: -52, z: 8 };
  const GRAVES: V2 = { x: -18, z: 40 };
  const LAKE: V2 = { x: 22, z: -16 };
  const CHAPEL: V2 = { x: 18, z: -52 };
  const DEN: V2 = { x: 66, z: 50 };
  const STONE = 0x9aa4b8;

  // ---------------------------------------------------------------- terrain
  edgeRise(b, 26, 18, [W_EXIT], 22, 23);
  const pass: V2[] = [{ x: -120, z: 60 }, { x: -100, z: 58 }, { x: -84, z: 46 }, { x: -70, z: 34 }, { x: -56, z: 28 }];
  const valley: V2[] = [{ x: -48, z: 28 }, { x: -30, z: 22 }, { x: -6, z: 16 }, { x: 14, z: 12 }, { x: 40, z: 14 }, { x: 60, z: 6 }, { x: 72, z: -16 }, { x: 78, z: -44 }, { x: 82, z: -70 }, { x: 86, z: -88 }];
  const graveLane: V2[] = [{ x: -40, z: 27 }, { x: -28, z: 33 }];
  const shoreRd: V2[] = [{ x: -6, z: 16 }, { x: -12, z: -4 }, { x: -16, z: -30 }, { x: 0, z: -44 }, { x: 12, z: -44 }];
  const northRd: V2[] = [{ x: -74, z: 33 }, { x: -81, z: 12 }, { x: -78, z: -12 }, { x: -66, z: -36 }, { x: -58, z: -46 }];
  b.road(pass, 4.5, 1);
  b.road(valley, 5, 1);
  b.road(graveLane, 3.5, 1);
  b.road(shoreRd, 3.5, 1);
  b.road(northRd, 3, 1);

  // Frozen lake: a basin below the water line with an icy rim.
  b.flat(LAKE, 14, { height: -10, soft: 10 });
  b.flat({ x: LAKE.x - 16, z: LAKE.z - 10 }, 7, { height: -9.5, soft: 8 });
  b.flat({ x: LAKE.x + 14, z: LAKE.z + 8 }, 7, { height: -9, soft: 7 });
  paint(b, 3, (x, z) => Math.max(disc(LAKE, 26, 6)(x, z), disc({ x: LAKE.x - 16, z: LAKE.z - 10 }, 18, 5)(x, z)), 3);

  // Rock spurs that break the valley into areas.
  const spur = { width: 10, height: 10, soft: 6, layer: 2, tint: 0x8a92a0 };
  ridge(b, [{ x: -124, z: -10 }, { x: -108, z: -12 }, { x: -96, z: -18 }], { ...spur, seed: 11 });
  ridge(b, [{ x: 124, z: 8 }, { x: 102, z: 14 }, { x: 88, z: 24 }], { ...spur, seed: 12 });
  ridge(b, [{ x: 24, z: 124 }, { x: 30, z: 100 }, { x: 40, z: 86 }], { ...spur, seed: 13 });
  ridge(b, [{ x: 36, z: -124 }, { x: 40, z: -100 }, { x: 34, z: -80 }], { ...spur, width: 8, seed: 14 });

  // ---------------------------------------------------------------- the pass from Sunscar
  b.flat({ x: -102, z: 58 }, 8, { layer: 1 });
  b.portal({ id: 'fh_to_sunscar', to: 'sunscar', toSpawn: 'fromFrosthollow', x: W_EXIT.x, z: W_EXIT.z, radius: 3.5, label: 'Sunscar Expanse', kind: 'exit' });
  b.spawn('fromSunscar', -101, 58, PI / 2);
  place(b, 'signpost', -98, 64, 0.5);
  place(b, 'brazier', -106, 64);
  place(b, 'brazier', -106, 53);
  lampsAlong(b, pass, 14, 3.4, { stagger: true, start: 12 });

  // ---------------------------------------------------------------- Abbey of the Hollow Bell (hub)
  // Chapel and bell tower close the north side; cloister walls west and
  // east; the courtyard opens south toward the camera and the road.
  b.flat({ x: ABBEY.x, z: ABBEY.z + 2 }, 17, { layer: 1, soft: 5 });
  place(b, 'house_c', ABBEY.x, ABBEY.z - 14, 0, { scale: 1.8, tint: STONE });
  place(b, 'tower', ABBEY.x + 13, ABBEY.z - 12, 0, { scale: 1.5, tint: STONE });
  wall(b, [{ x: ABBEY.x - 8, z: ABBEY.z - 18 }, { x: ABBEY.x - 14, z: ABBEY.z - 18 }, { x: ABBEY.x - 14, z: ABBEY.z + 12 }], { tint: STONE, towerScale: 0.7, openEnds: true });
  wall(b, [{ x: ABBEY.x + 16, z: ABBEY.z - 9 }, { x: ABBEY.x + 16, z: ABBEY.z + 12 }], { tint: STONE, towerScale: 0.7, openEnds: true });
  place(b, 'tower', ABBEY.x - 14, ABBEY.z + 13, 0, { scale: 0.75, tint: STONE });
  place(b, 'tower', ABBEY.x + 16, ABBEY.z + 13, 0, { scale: 0.75, tint: STONE });
  place(b, 'pillar_intact', ABBEY.x - 6, ABBEY.z + 16, 0, { scale: 1.3, tint: STONE });
  place(b, 'pillar_intact', ABBEY.x + 6, ABBEY.z + 16, 0, { scale: 1.3, tint: STONE });
  place(b, 'brazier', ABBEY.x - 8.5, ABBEY.z + 18);
  place(b, 'brazier', ABBEY.x + 8.5, ABBEY.z + 18);
  place(b, 'statue_hero', ABBEY.x - 9, ABBEY.z - 3, PI / 2, { scale: 1.2, tint: 0x7a8498 });
  place(b, 'banner_pole', ABBEY.x - 5, ABBEY.z - 5.5, 0, { tint: 0x3a4a6a });
  place(b, 'banner_pole', ABBEY.x + 5, ABBEY.z - 5.5, 0, { tint: 0x3a4a6a });
  b.npc('abbess_vira', ABBEY.x, ABBEY.z - 3, 0);
  const stall = { x: ABBEY.x - 10.5, z: ABBEY.z + 5 };
  placeFacing(b, 'market_stall', stall, { x: ABBEY.x, z: ABBEY.z + 5 }, { tint: 0x8a6a5a });
  const olga = { x: ABBEY.x - 7, z: ABBEY.z + 7.5 };
  b.npc('trader_olga', olga.x, olga.z, angleTo(olga, { x: ABBEY.x, z: ABBEY.z + 10 }));
  place(b, 'barrel', ABBEY.x - 12, ABBEY.z + 9);
  place(b, 'crate', ABBEY.x - 12, ABBEY.z + 1, 0.3);
  b.interact({ id: 'fh_waystone_abbey', kind: 'waystone', x: ABBEY.x + 9, z: ABBEY.z + 5, data: { name: 'Abbey of the Hollow Bell' } });
  b.spawn('start', ABBEY.x, ABBEY.z + 9, PI);
  place(b, 'bench', ABBEY.x + 13.5, ABBEY.z - 2, -PI / 2);
  place(b, 'well', ABBEY.x + 4, ABBEY.z + 1);
  // Hunters' camp outside the west cloister.
  const bjorn = { x: ABBEY.x - 19, z: ABBEY.z + 14 };
  b.npc('hunter_bjorn', bjorn.x, bjorn.z, angleTo(bjorn, { x: ABBEY.x - 8, z: ABBEY.z + 24 }));
  place(b, 'tent', ABBEY.x - 22, ABBEY.z + 3, 0.5, { tint: 0x6a5a4a });
  place(b, 'campfire', ABBEY.x - 22, ABBEY.z + 19);
  place(b, 'log', ABBEY.x - 24.5, ABBEY.z + 12, 1.2);
  place(b, 'crate', ABBEY.x - 18, ABBEY.z + 5, 0.4);
  b.prop('bones', ABBEY.x - 20, ABBEY.z + 8, { rot: 1, noShadow: true });
  lampsAlong(b, valley, 13, 3.6, { stagger: true, start: 4, end: 80 });

  // ---------------------------------------------------------------- graveyard
  b.flat(GRAVES, 15, { layer: 2, soft: 4 });
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 4; col++) {
      if ((row + col) % 5 === 4) continue;
      place(b, 'sarcophagus', GRAVES.x - 9 + col * 6, GRAVES.z - 7 + row * 6, rng.range(-0.15, 0.15), { tint: 0x8a8e9a });
    }
  }
  // Low walls frame the north and west sides (the south stays open to the camera); an arch spans the lane.
  archway(b, { x: -32.8, z: 30.6 }, Math.atan2(12, 6), { tint: 0x7a7e8a });
  wall(b, [{ x: GRAVES.x - 11, z: GRAVES.z - 15 }, { x: GRAVES.x + 12, z: GRAVES.z - 15 }], { scale: 0.5, tint: 0x7a7e8a, tower: 'pillar_intact', towerScale: 0.6 });
  wall(b, [{ x: GRAVES.x - 16, z: GRAVES.z - 5 }, { x: GRAVES.x - 16, z: GRAVES.z + 13 }], { scale: 0.5, tint: 0x7a7e8a, tower: 'pillar_intact', towerScale: 0.6 });
  for (const [dx, dz] of [[-14, 3], [12, -10], [13, 9], [-3, 13]] as [number, number][]) place(b, 'tree_dead', GRAVES.x + dx, GRAVES.z + dz, rng.range(0, TAU), { scale: rng.range(1, 1.4), tint: 0x5a5a60 });
  for (const [dx, dz] of [[-14, -4], [14, 2], [6, -13]] as [number, number][]) place(b, 'pillar_ruin', GRAVES.x + dx, GRAVES.z + dz, rng.range(0, TAU), { tint: STONE });
  place(b, 'altar', GRAVES.x, GRAVES.z - 12, 0, { tint: 0x7a7e8a });
  place(b, 'lamp_post', GRAVES.x - 7, GRAVES.z - 13);
  place(b, 'lamp_post', GRAVES.x + 7, GRAVES.z - 13);
  b.interact({ id: 'fh_lore_graves', kind: 'lore', x: GRAVES.x - 4, z: GRAVES.z - 10, data: { lore: 'bellringers_grave' } });
  b.interact({ id: 'fh_chest_graves', kind: 'chest', x: GRAVES.x + 11, z: GRAVES.z + 4, data: { loot: 'graveyard_chest' } });

  // ---------------------------------------------------------------- frozen lake & drowned chapel
  for (const p of openSpots(b, 2, { minX: LAKE.x - 30, minZ: LAKE.z - 30, maxX: LAKE.x + 30, maxZ: LAKE.z + 30 }, 14, (p) => dist(p, LAKE) > 20 && dist(p, LAKE) < 25 && polylineDistance(p, valley) > 4)) {
    b.interact({ id: `fh_fish_${b.interactables.filter((i) => i.kind === 'fish').length}`, kind: 'fish', x: p.x, z: p.z, data: { skill: 'fishing' } });
  }
  b.flat(CHAPEL, 12, { layer: 2, soft: 4 });
  ringOf(b, 'pillar_ruin', CHAPEL, 9, 8, { phase: PI / 8, scale: 1.3, tint: STONE, skip: (a) => Math.cos(a) > 0.8 });
  place(b, 'altar', CHAPEL.x, CHAPEL.z - 6, 0, { tint: STONE });
  place(b, 'dungeon_wall', CHAPEL.x - 7, CHAPEL.z - 10, 0.5, { tint: STONE, scale: 0.8 });
  place(b, 'brazier', CHAPEL.x - 3, CHAPEL.z - 6.5);
  place(b, 'brazier', CHAPEL.x + 3, CHAPEL.z - 6.5);
  b.interact({ id: 'fh_lore_chapel', kind: 'lore', x: CHAPEL.x + 2, z: CHAPEL.z - 3.5, data: { lore: 'drowned_bell' } });
  b.interact({ id: 'fh_chest_chapel', kind: 'chest', x: CHAPEL.x - 4, z: CHAPEL.z - 4, data: { loot: 'abbey_chest' } });

  // North-west ruins below the overlook.
  const NWR: V2 = { x: -56, z: -56 };
  b.flat(NWR, 11, { layer: 2 });
  place(b, 'tower', NWR.x - 5, NWR.z - 6, 0, { scale: 1.1, tint: 0x7a8090 });
  place(b, 'dungeon_wall', NWR.x + 3, NWR.z - 8, 0.2, { tint: STONE });
  place(b, 'dungeon_wall', NWR.x + 8, NWR.z - 1, 1.6, { tint: STONE, scale: 0.7 });
  place(b, 'pillar_ruin', NWR.x - 8, NWR.z + 4, 0.5, { tint: STONE });
  b.interact({ id: 'fh_vista_overlook', kind: 'vista', x: -30, z: -84, data: { name: 'Hollow Bell Overlook' } });
  b.interact({ id: 'fh_heart', kind: 'heart', x: -92, z: -88, data: { name: 'Heart of the Frost' } });

  // ---------------------------------------------------------------- Rimefang's den (east forest)
  b.flat(DEN, 13, { soft: 4 });
  ringOf(b, 'stump', DEN, 14, 9, { phase: 0.3, scale: 1.2 });
  for (let i = 0; i < 6; i++) {
    const a = rng.range(0, TAU);
    const r = rng.range(3, 10);
    b.prop('bones', DEN.x + Math.sin(a) * r, DEN.z + Math.cos(a) * r, { rot: rng.range(0, TAU), scale: rng.range(0.9, 1.5), noShadow: true });
  }
  b.pack({ id: 'fh_fieldboss', monsters: [{ def: 'rimefang_alpha', count: 1 }], x: DEN.x, z: DEN.z, radius: 2, level: 28, respawn: 600, tags: ['fieldBoss'] });

  // ---------------------------------------------------------------- highlands & Frostfang Keep (north-east)
  b.interact({ id: 'fh_waystone_highlands', kind: 'waystone', x: 74, z: -58, data: { name: 'Highland Cairn' } });
  b.interact({ id: 'fh_chest_yeti', kind: 'chest', x: 104, z: -52, data: { loot: 'yeti_hoard' } });
  b.flat({ x: KEEP.x, z: KEEP.z + 8 }, 11, { layer: 2 });
  b.portal({ id: 'fh_to_keep', to: 'keep', toSpawn: 'start', x: KEEP.x, z: KEEP.z, radius: 3, label: 'Frostfang Keep', kind: 'dungeon', minLevel: 27 });
  b.spawn('fromKeep', KEEP.x - 2, KEEP.z + 12, 0);
  place(b, 'gate_iron', KEEP.x, KEEP.z - 3.6, 0, { scale: 1.3, tint: 0x5a6070 });
  place(b, 'tower', KEEP.x - 8, KEEP.z - 3, 0, { scale: 1.2, tint: 0x6a7080 });
  place(b, 'tower', KEEP.x + 8, KEEP.z - 3, 0, { scale: 1.2, tint: 0x6a7080 });
  place(b, 'brazier', KEEP.x - 5, KEEP.z + 4);
  place(b, 'brazier', KEEP.x + 5, KEEP.z + 4);
  place(b, 'banner_pole', KEEP.x - 12, KEEP.z + 3, 0, { tint: 0x2a3a5a });
  place(b, 'banner_pole', KEEP.x + 12, KEEP.z + 3, 0, { tint: 0x2a3a5a });
  place(b, 'statue_hero', KEEP.x - 7, KEEP.z + 13, 0, { scale: 1.3, tint: 0x6a7488 });
  place(b, 'statue_hero', KEEP.x + 7, KEEP.z + 13, 0, { scale: 1.3, tint: 0x6a7488 });
  lampsAlong(b, valley, 12, 3.6, { stagger: true, start: 170, model: 'brazier' });

  // ---------------------------------------------------------------- monsters (levels climb west → north-east)
  const pack = (id: string, x: number, z: number, level: number, monsters: [string, number][], radius = 6, respawn = 45) =>
    b.pack({ id, monsters: monsters.map(([def, count]) => ({ def, count })), x, z, radius, level, respawn });
  pack('fh_wolf_a', -97, 30, 21, [['frost_wolf', 3]]);
  pack('fh_wolf_b', -80, 80, 22, [['frost_wolf', 4]]);
  pack('fh_wolf_c', -42, 72, 22, [['frost_wolf', 3]]);
  pack('fh_ghoul_a', GRAVES.x, GRAVES.z + 14, 23, [['frost_ghoul', 4]]);
  pack('fh_ghoul_b', 2, 44, 24, [['frost_ghoul', 5]], 7);
  pack('fh_ghoul_c', -32, 56, 24, [['frost_ghoul', 3], ['ice_wraith', 1]], 7, 60);
  pack('fh_ruins_a', -82, -42, 25, [['frost_ghoul', 4]]);
  pack('fh_ruins_b', NWR.x + 2, NWR.z + 3, 26, [['ice_wraith', 3]], 6, 60);
  pack('fh_wraith_a', CHAPEL.x, CHAPEL.z + 4, 25, [['ice_wraith', 3]], 6, 60);
  pack('fh_wraith_b', 46, -44, 26, [['ice_wraith', 2], ['frost_ghoul', 3]], 7, 60);
  pack('fh_wolf_d', 40, 36, 25, [['frost_wolf', 4]]);
  pack('fh_wolf_e', 88, 72, 27, [['frost_wolf', 5]], 7);
  pack('fh_yeti_a', 54, -72, 27, [['frost_yeti', 1], ['frost_wolf', 2]], 7, 90);
  pack('fh_yeti_b', 100, -44, 28, [['frost_yeti', 2]], 6, 90);
  pack('fh_keep_a', 68, -88, 29, [['frost_ghoul', 4], ['ice_wraith', 2]], 7, 60);
  pack('fh_keep_b', 96, -78, 30, [['frost_yeti', 1], ['frost_ghoul', 3]], 7, 90);

  // ---------------------------------------------------------------- gathering & collectibles
  const all = { minX: -100, minZ: -100, maxX: 100, maxZ: 100 };
  lifeNodes(b, 'fh', [
    { kind: 'herb', count: 6, area: all, minDist: 26 },
    { kind: 'timber', count: 6, area: all, minDist: 26 },
    { kind: 'ore', count: 5, area: all, minDist: 28 },
    { kind: 'dig', count: 4, area: all, minDist: 30 },
    { kind: 'seed', count: 10, area: all, minDist: 16 },
  ]);

  // ---------------------------------------------------------------- scenery
  b.forestBorder([{ model: 'tree_pine_snow', weight: 4, scale: [1, 1.6] }, { model: 'rock_c', weight: 1, scale: [1.1, 1.8] }], 16, 4.4, [W_EXIT]);
  const inner = { minX: -102, minZ: -102, maxX: 102, maxZ: 102 };
  const clear = (p: V2) => isOpen(b, p, 0.8) && dist(p, LAKE) > 24 && dist(p, ABBEY) > 26;
  const forests: [V2, number][] = [
    [{ x: -80, z: 76 }, 26],
    [{ x: -30, z: 84 }, 22],
    [{ x: 80, z: 60 }, 28],
    [{ x: -80, z: -60 }, 24],
    [{ x: 60, z: -30 }, 16],
    [{ x: 0, z: 90 }, 16],
  ];
  for (const [c, r] of forests) {
    const box = { minX: c.x - r, minZ: c.z - r, maxX: c.x + r, maxZ: c.z + r };
    const n = Math.round((Math.PI * r * r) / 22);
    b.scatterProps([{ model: 'tree_pine_snow', weight: 5, collider: { r: 0.6 }, scale: [0.9, 1.5] }, { model: 'tree_dead', weight: 0.4, collider: { r: 0.5 }, scale: [0.9, 1.3] }], n, box, 3.8, (p) => dist(p, c) < r && clear(p), 2);
  }
  b.scatterProps([{ model: 'tree_pine_snow', weight: 4, collider: { r: 0.6 }, scale: [0.8, 1.4] }, { model: 'tree_dead', weight: 1, collider: { r: 0.5 }, scale: [0.8, 1.2] }], 70, inner, 8, clear, 3);
  b.scatterProps(
    [
      { model: 'rock_a', weight: 2, collider: { r: 0.9 }, scale: [0.7, 1.6] },
      { model: 'rock_b', weight: 2, collider: { r: 0.9 }, scale: [0.6, 1.4] },
      { model: 'rock_c', weight: 1, collider: { r: 1.2 }, scale: [0.8, 1.5] },
      { model: 'ice_crystal', weight: 1.2, collider: { r: 0.7 }, scale: [0.7, 1.4] },
    ],
    50, inner, 9, (p) => isOpen(b, p, 1), 3,
  );
  const lakeBox = { minX: LAKE.x - 32, minZ: LAKE.z - 32, maxX: LAKE.x + 32, maxZ: LAKE.z + 32 };
  b.scatterProps([{ model: 'ice_crystal', weight: 1, collider: { r: 0.7 }, scale: [0.6, 1.3] }], 18, lakeBox, 5, (p) => dist(p, LAKE) > 21 && dist(p, LAKE) < 30 && isOpen(b, p, 1), 2);
  b.scatterProps(
    [
      { model: 'bush', weight: 2, noShadow: true, scale: [0.7, 1.1] },
      { model: 'grass_tuft', weight: 3, noShadow: true, scale: [0.7, 1.1] },
      { model: 'stump', weight: 0.5, collider: { r: 0.5 } },
      { model: 'log', weight: 0.5, collider: { w: 2.5, d: 0.7 } },
      { model: 'bones', weight: 0.4, noShadow: true },
    ],
    180, inner, 3.2, (p) => isOpen(b, p, 0.5), 0.5,
  );

  return b.build();
}
